import { useCallback, useEffect, useRef, useState } from 'react';

import { Link } from 'react-router-dom';

import { useAppDispatch as useDispatch, useAppSelector as useSelector } from 'dux/app/hooks';

import styled from '@emotion/styled';

import Image from 'next/image';

import Button from 'Components/LegacyButton';

import { introductionContent } from 'assets/content/landingPages/skincare';
import skinReorder from 'assets/images/account/cart-intermediary-skincare.jpeg';
import newBadgeSkincare from 'assets/images/landingPages/skincare/new-badge-static.svg';

import { trackHeapEvent } from 'dux/tracking/actions';
import {
  getHasCompletedSkincareConsultation,
  getHasDraftSkincareConsultation,
  getHasSeenSkincarePromotionalModal,
} from 'dux/user/selectors';

import AutoPlayVideo from './AutoPlayVideo';
import BasicModal from './BasicModal';
import Typography from './Typography';

const StyledBasicModal = styled(BasicModal)`
  display: grid;
  grid-template-rows: minmax(160px, 1fr) auto;
  padding: 0;
  ${props => props.theme.breakpoints.match('sm')} {
    padding: 0;
    height: 90vh;
    max-height: 600px;
  }
`;

const BageContainer = styled.div`
  position: relative;
`;

const BadgeEarlyAccess = styled(Image)`
  position: absolute;
  right: ${({ theme }) => theme.spacing.s32}px;
  bottom: -${({ theme }) => theme.spacing.s32}px;

  ${props => props.theme.breakpoints.match('sm')} {
    right: ${({ theme }) => theme.spacing.s16}px;
    bottom: -${({ theme }) => theme.spacing.s24}px;
  }
`;

const StyledAutoPlayVideo = styled(AutoPlayVideo)`
  object-fit: cover;
  aspect-ratio: 960/750;
  width: 100%;
  height: 100%;
`;

const Container = styled.div`
  padding-block: ${({ theme }) => theme.spacing.s32}px;
  padding-inline: ${({ theme }) => theme.spacing.s40}px;
  height: auto;
  ${props => props.theme.breakpoints.match('sm')} {
    padding-block: min(${({ theme }) => theme.spacing.s24}px, 4vh)
      min(${({ theme }) => theme.spacing.s32}px, 4vh);
    padding-inline: ${({ theme }) => theme.spacing.s32}px ${({ theme }) => theme.spacing.s24}px;
  }
`;

const PromotionalModalSkincare = () => {
  const dispatch = useDispatch();

  const [isOpen, setIsOpen] = useState(true);
  const hasModalBeenClosed = useRef(false);

  const hasSeenSkincarePromotionalModal = useSelector(getHasSeenSkincarePromotionalModal);
  const hasDraftSkincareConsultation = useSelector(getHasDraftSkincareConsultation);
  const hasCompletedSkincareConsultation = useSelector(getHasCompletedSkincareConsultation);

  const hasPreviouslySeenSkincareModal = useRef(hasSeenSkincarePromotionalModal); // not updated after first render

  const hasPreviouslySeenSkincare =
    hasPreviouslySeenSkincareModal.current ||
    hasDraftSkincareConsultation ||
    hasCompletedSkincareConsultation;

  useEffect(() => {
    let timeout;
    if (!hasPreviouslySeenSkincare) {
      dispatch(trackHeapEvent('Subscriber only popup - viewed')); // send event when displaying the Modal
      timeout = setTimeout(() => {
        /* using a ref here because we don't want isOpen to be a useEffect dependency */
        if (!hasModalBeenClosed.current) {
          dispatch(trackHeapEvent('Subscriber only popup - 2 sec viewed'));
        }
      }, 2000);
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [hasPreviouslySeenSkincare]);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    hasModalBeenClosed.current = true;
  }, [setIsOpen]);

  if (hasPreviouslySeenSkincare) return null;

  return (
    <StyledBasicModal isOpen={isOpen} onClose={closeModal}>
      <BageContainer>
        <StyledAutoPlayVideo
          desktopImage={skinReorder}
          desktopVideo={introductionContent?.video?.desktop}
          mobileVideo={introductionContent?.video?.mobile}
        />
        <BadgeEarlyAccess
          alt="Early access, subscriber exclusive"
          height={102.5}
          src={newBadgeSkincare}
          width={102.5}
        />
      </BageContainer>
      <Container data-testid="skincare-promotional-modal-texts">
        <Typography markupName="h3" paragraph variant="h2">
          Introducing Custom&nbsp;Skincare
        </Typography>
        <Typography paragraph variant="p1">
          We’re giving haircare subscribers exclusive access to our new custom skincare line.
          To&nbsp;access skincare, you must be signed in to your&nbsp;account.
        </Typography>
        <Button
          Component={Link}
          data-click="explore-skincare"
          data-from="subscriber-only-popup"
          data-testid="skincare-modal-cta"
          noMargin
          onClick={closeModal}
          to="/pages/skincare"
          variant="vert"
        >
          Explore Skincare
        </Button>
      </Container>
    </StyledBasicModal>
  );
};

export default PromotionalModalSkincare;
